import axios, { AxiosError, AxiosHeaders, CanceledError } from 'axios';
import type { AxiosAdapter, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import type { HttpPort, HttpRequest } from '../chat/core/ports';
import { HttpTimeoutError } from '../chat/platform/http';

const toRequest = (config: InternalAxiosRequestConfig): HttpRequest => {
    const body = config.data as HttpRequest['body'];
    const headers: Record<string, string> = {};
    for (const [name, value] of Object.entries(AxiosHeaders.from(config.headers).toJSON())) {
        if (value === undefined || value === null) continue;
        // The browser writes a multipart Content-Type with its own boundary.
        if (body instanceof FormData && name.toLowerCase() === 'content-type') continue;
        headers[name] = String(value);
    }
    return {
        method: (config.method ?? 'get').toUpperCase(),
        url: axios.getUri(config),
        headers,
        body,
        signal: config.signal as AbortSignal | undefined,
        timeoutMs: config.timeout ?? 0,
        credentials: config.withCredentials ? 'include' : 'same-origin',
    };
};

/**
 * Axios over the app's HttpPort, so the app's axios calls and the chat core share one transport
 * and its session and offline handling. Axios still builds the URL and body before this runs and
 * parses the JSON after it, so its callers see no change.
 */
export const httpPortAdapter =
    (port: HttpPort): AxiosAdapter =>
    async (config) => {
        let response;
        try {
            response = await port.fetch(toRequest(config));
        } catch (error) {
            if (config.signal?.aborted) throw new CanceledError(undefined, undefined, config);
            if (error instanceof HttpTimeoutError) {
                throw new AxiosError(`timeout of ${config.timeout}ms exceeded`, AxiosError.ECONNABORTED, config);
            }
            throw new AxiosError('Network Error', AxiosError.ERR_NETWORK, config);
        }

        const contentType = response.headers.get('content-type');
        let data: unknown;
        if (config.responseType === 'blob') {
            data = new Blob([await response.arrayBuffer()], { type: contentType ?? '' });
        } else if (config.responseType === 'arraybuffer') {
            data = await response.arrayBuffer();
        } else {
            data = await response.text();
        }
        const headers = new AxiosHeaders();
        if (contentType) headers.set('content-type', contentType);
        const date = response.headers.get('date');
        if (date) headers.set('date', date);

        const result: AxiosResponse = { data, status: response.status, statusText: '', headers, config };
        if (!config.validateStatus || config.validateStatus(response.status)) return result;
        throw new AxiosError(
            `Request failed with status code ${response.status}`,
            response.status >= 500 ? AxiosError.ERR_BAD_RESPONSE : AxiosError.ERR_BAD_REQUEST,
            config,
            undefined,
            result,
        );
    };
